import {RunConfigService, UrlService, events, storage} from '@nuskin/ns-util';
import axios from 'axios';
import { UserService } from '@nuskin/ns-account';

export default {
    init,
    checkForWaits,
    getActiveTicketEvents,
    getEventStatus,
    initEventsForConnect,
    logAnalyticInfo
};

const PRE_EVENT = 1;
const IN_EVENT = 2;
const POST_EVENT = 4;
const ACTIVE_EVENTS = 'active-sales-events';
const ONE_MINUTE = 1000 * 60;
const THREE_MINUTES = ONE_MINUTE * 3;
const ONE_HOUR = ONE_MINUTE * 60;
const ONE_DAY = ONE_HOUR * 24;
const TWENTY_FOUR_DAYS = ONE_DAY * 24;
const TICKET_TTL = ONE_DAY * 35;    // tickets will get removed when event is over. This is used as a fail safe.
const ACTIVE_EVENT_WITHIN = 30;     // get active events starting within this amount of minutes
const ACTIVE_EVENT_TTL = ONE_MINUTE * 14;
const INITIALIZING = 'initializing';
const EXTERNAL_INITIALIZING = 'initializing-external';

// Contains a map of all event tickets that are stored in localStorage.
let eventTicketInfoMap;

// Contains a map of event names whose ticket status is IN_EVENT.
let activeEventMap = {};

// Contains market sales event configuration. Force loaded periodically to capture event status changes.
let mktEventConfigMap;

// will contain added event names. Used for testing purposes
let seParamEvents;

// use by the wakeup check call.
let intervalId;

// used to for timing the next loading of active events
let activeEventsTimerId;

// flag to determine if market sales event configuration is to be polled.
let pollMarketConfig = false;

// will contain values for things that are happening that other code points might be interested
//  in waithing for them to finish. Defaults with INITIALIZING in the list.
let waitChecks = [INITIALIZING];

//=========================================================================================================//
//
//  PUBLIC FUNCTIONS
//
//=========================================================================================================//

/**
 * INIT_EVENT_TICKET is published by pages that have ns-event-button(s) or ns-event-promo(s)
 *  components on them.  This call initializes an event ticket regardless if the market
 *  is associated with the event or not.
 *
 * @param eventName - event to initialize a ticket for.
 * @param refresh - force refresh of an existing ticket.
 */
events.subscribe(events.salesevent.INIT_EVENT_TICKET, async (eventName, refresh = false) => {
    await checkForWaits();

    if (eventName && await _initData()) {
        if (mktEventConfigMap[eventName]) {
            _initEventTicketInfo(eventName, refresh)
                .catch((err) => {
                    console.error(`Error initializing event ticket for ${eventName}.`, err)
                });
        }
    }
});

/**
 * GO_TO_EVENT_PAGE is published by the ns-event-button component which becomes active
 *  when it receives an event.salesevent.IN_EVENT message. It first checks the
 *  event ticket to make sure the event is actually active and then goes to the
 *  page set up in market configuration.
 *
 * @param eventName
 */
events.subscribe(events.salesevent.GO_TO_EVENT_PAGE, eventName => {
    if (eventName) {
        let eventTicket = eventTicketInfoMap[eventName];

        if (eventTicket) {
            if (_decodeStatus(eventTicket.ticket.status).eventStatus === IN_EVENT) {
                window.location = _getEventUrl(eventTicket.eventName);
            }
        }
    }
});

/**
 * POLL_MARKET_CONFIG is published by pages that need to know when an event has been paused
 *  or turned off.  This is done in market configuration
 */
events.subscribe(events.salesevent.POLL_MARKET_CONFIG, () => {
    pollMarketConfig = true;
});

/**
 * This can be called in situations where the flow of the code needs to
 *  wait for AWS sales event ticket checks to finish.
 *
 * @returns {Promise<void>}
 */
async function checkForWaits() {
    let cnt = 0;
    const waits = waitChecks.join();

    while (waitChecks.length > 0 && cnt < 100) {
        await _wait(200);
        cnt += 1;
    }
    if (cnt > 0) {
        events.tag('salesevent-service-wait', {waitCount: cnt, waitTime: cnt*150, waitingOn: waits});
    }
}

function getActiveTicketEvents() {
    const events = [];
    const ticketMap = _getLocalStorageTickets();

    Object.values(ticketMap).forEach((ticketInfo) => {
        const status = _decodeStatus(ticketInfo.ticket.status);
        if (status.preWaitTime <= 30000 && status.endWaitTime >= 0) {
            events.push(ticketInfo.eventName);
        }
    });

    return events;
}

async function getEventStatus(eventsToCheck = []) {
    const eventStatus = {};
    const promises = [];

    eventsToCheck.forEach((toCheck) => {
        promises.push(_checkForMissingTicket(toCheck));
    });

    await Promise.allSettled(promises);

    eventsToCheck.forEach((eventName) => {
        let activeStatus = false;
        const ticketInfo = eventTicketInfoMap[eventName];
        if (ticketInfo && ticketInfo.ticket) {
            const status = _decodeStatus(ticketInfo.ticket.status);
            if ((status.eventStatus === PRE_EVENT && status.preWaitTime <= 30000) ||
                status.eventStatus === IN_EVENT) {
                activeStatus = true;
            }
        }
        eventStatus[eventName] = activeStatus;
    });

    return eventStatus;
}

function logAnalyticInfo(tag, eventName, data = {}) {
    const user = UserService.getUser();
    const eventTicketInfo = eventTicketInfoMap[eventName];
    const lsTicket = storage.getItem(_getTicketName(eventName));
    const infoObj = Object.assign({
        userId: user ? user.id : '',
        eventName: eventName
    }, data);

    if (eventTicketInfo) {
        infoObj.inMemoryTicketInfo = eventTicketInfo;
    }
    if (lsTicket) {
        infoObj.lsTicket = lsTicket;
    }

    events.tag(tag, infoObj);
}

//=========================================================================================================//
//
//  PRIVATE FUNCTIONS
//
//=========================================================================================================//

//=========================================================================================================//
//
//  Functions for handling tickets
//
//=========================================================================================================//

/**
 * Initializes a ticket for the eventName passed in. The ticket may already exist
 *  in localStorage or it will get it from the AWS service
 *
 * @param eventName - event to get a ticket for.
 * @param refreshTicket - will force refreshing an existing ticket from the server.
 */
let initEventTicketCallMap = {};
async function _initEventTicketInfo(eventName, refreshTicket = false) {
    let initInfo = initEventTicketCallMap[eventName];
    if (!initInfo) {
        initInfo = {initialized: false, refreshTime: 0};
        initEventTicketCallMap[eventName] = initInfo;
    }

    if (!initInfo.initialized || (refreshTicket && initInfo.refreshTime + 1500 < Date.now())) {
        initInfo.initialized = true;

        let eventTicketInfo = eventTicketInfoMap[eventName],
            useServerWaitTime = false;

        _addWaitCheck(eventName);
        if (!eventTicketInfo) {
            eventTicketInfo = _createEventTicketInfo(eventName);
            eventTicketInfoMap[eventName] = eventTicketInfo;
        }

        if (!eventTicketInfo.ticket || refreshTicket) {
            initInfo.refreshTime = Date.now();
            await _getRemoteTicket(eventTicketInfo);
            useServerWaitTime = true;
        }
        _marketStatusCheck(eventTicketInfo);
        _checkStatus(eventTicketInfo, useServerWaitTime);
        _removeWaitCheck(eventName);
    }
}

/**
 * Checks the status of a ticket and publishes events based on that status.
 *
 * @param eventTicketInfo - event ticket info object containing the ticket and other status info.
 * @param useServerWaitTime - true if the wait (timer) time passed back from the server
 *                                  is to be use.
 *                            false if the time is to be calculated
 */
function _checkStatus(eventTicketInfo, useServerWaitTime) {
    let ticket = eventTicketInfo.ticket;

    if (ticket && eventTicketInfo.needsStatusCheck) {
        let status = _decodeStatus(ticket.status),
            timerWait = _getWaitTime(status, useServerWaitTime),
            actualWait = _getWaitTime(status, useServerWaitTime, true),
            eventInfo = eventTicketInfoMap[ticket.eventName];

        _clearTimeout(eventInfo)

        switch (status.eventStatus) {
            case PRE_EVENT:
                events.setValue(
                    events.salesevent.PRE_EVENT,
                    [ticket.eventName, eventTicketInfo.paused, {fromServer: useServerWaitTime, waitTime: actualWait}]
                );

                eventInfo.timerId = setTimeout(_timerCallback(ticket.eventName), timerWait);
                if (useServerWaitTime && timerWait < 30000) {
                    // if the wait time is base off of what the server passed back then
                    //  don't call the server to update the ticket at the end of this timer
                    eventInfo.goodToGo = true;
                }
                break;
            case IN_EVENT:
                if (timerWait > 0) {
                    if (eventTicketInfo.paused) {
                        delete activeEventMap[ticket.eventName];
                    } else {
                        activeEventMap[ticket.eventName] = ticket.eventName;
                    }
                    events.setValue(events.salesevent.ACTIVE_EVENTS, [Object.keys(activeEventMap)]);
                    events.setValue(
                        events.salesevent.IN_EVENT,
                        [ticket.eventName, eventTicketInfo.paused, {fromServer: useServerWaitTime, waitTime: actualWait}]
                    );
                    logAnalyticInfo('in-event', ticket.eventName);
                    eventInfo.timerId = setTimeout(_timerCallback(ticket.eventName), timerWait);
                } else {
                    _changeStatus(eventTicketInfo, POST_EVENT);
                    _checkStatus(eventTicketInfo, false); // to send post event and clear ticket.
                }
                break;
            case POST_EVENT:
                delete activeEventMap[ticket.eventName];
                delete eventTicketInfoMap[ticket.eventName];
                events.setValue(events.salesevent.ACTIVE_EVENTS, [Object.keys(activeEventMap)]);
                storage.removeItem(_getTicketName(ticket.eventName));
                events.setValue(events.salesevent.POST_EVENT, [ticket.eventName]);
                break;
        }
        eventTicketInfo.needsStatusCheck = false;
    }
}

/**
 * This determines the time to wait before the ticket status is checked again.
 *
 * @param {*} status - the decoded ticket status
 * @param {*} useServerWaitTime - true to use the wait time in the ticket status
 * @param {boolean} actual - get actual wait time.
 */
function _getWaitTime(status, useServerWaitTime, actual = false) {
    let retVal = -1;

    switch (status.eventStatus) {
        case PRE_EVENT: {
            let waitTime = useServerWaitTime ? status.serverWaitTime : status.preWaitTime;
            retVal = actual || waitTime < THREE_MINUTES ? waitTime : Math.floor(waitTime * 0.4);
            break;
        }
        case IN_EVENT:
            retVal = useServerWaitTime ? status.serverWaitTime : status.endWaitTime;
            break;
    }

    // 24 days is the limit for this call. If retVal is negative then just return 0.
    return actual ? retVal : Math.max(Math.min(retVal, TWENTY_FOUR_DAYS), 0);
}

/**
 * Decodes the values that are in the ticket status.
 *
 * @param {*} ticketStatus - status field of the event ticket
 */
function _decodeStatus(ticketStatus) {
    let values = ticketStatus.split('-'),
        entryTime = _decodeValue(values[1]),
        endTime = _decodeValue(values[2]),
        now = Date.now();

    return {
        origValues: values,
        serverWaitTime: _decodeValue(values[0]),
        entryTime: entryTime,
        preWaitTime: entryTime - now,
        endTime: endTime,
        endWaitTime: endTime - now,
        eventStatus: _decodeValue(values[3]) & 7
    }
}

/**
 * Returns a callback function for a timer.  When a timer fires the status of the
 *  event's ticket is examined to determine further action.
 *
 * @param _eventName - event name pertaining to the returned callback function.
 * @returns {function} the function passed to a setTimeout call
 */
function _timerCallback(_eventName) {
    let eventName = _eventName;

    return async () => {
        let eventTicketInfo = eventTicketInfoMap[eventName];

        if (eventTicketInfo.timerId) {
            eventTicketInfo.timerId = null;
        }

        // noinspection FallThroughInSwitchStatementJS
        switch (_decodeStatus(eventTicketInfo.ticket.status).eventStatus) {
            case PRE_EVENT: {
                let userServerWaitTime = true;

                _addWaitCheck(eventName);
                if (eventTicketInfo.goodToGo || eventTicketInfo.ticket.manualTicket) {
                    _changeStatus(eventTicketInfo, IN_EVENT);
                    userServerWaitTime = false;
                } else {
                    // update ticket from AWS
                    await _getRemoteTicket(eventTicketInfo);
                }
                _checkStatus(eventTicketInfo, userServerWaitTime);
                _removeWaitCheck(eventName);
                break;
            }
            case IN_EVENT: {
                let userServerWaitTime = true;

                _addWaitCheck(eventName);
                eventTicketInfo.needsStatusCheck = true;
                if (!eventTicketInfo.ticket.manualTicket) {
                    await _getRemoteTicket(eventTicketInfo);
                } else {
                    eventTicketInfo.needsStatusCheck = true;
                    userServerWaitTime = false;
                }
                _checkStatus(eventTicketInfo, userServerWaitTime);
                _removeWaitCheck(eventName);
                break;
            }
            case POST_EVENT:
                // probably will not ever be POST_EVENT here but check anyway
                _checkStatus(eventTicketInfo, false);
                break;
        }
    }
}

/**
 * Scans localStorage for event tickets and loads them into memory.
 *
 * @private
 */
function _loadLocalEventTickets() {
    if (!eventTicketInfoMap) {
        eventTicketInfoMap = _getLocalStorageTickets();
    }
}

/**
 * Loads sales event tickets from localStorage
 *
 * @private {object}
 */
function _getLocalStorageTickets() {
    const map = {};

    Object.keys(localStorage).forEach(lsKey => {
        if (lsKey.endsWith('-event-ticket')) {
            let ticket = getStorageItem(lsKey);

            if (ticket && ticket.eventName && ticket.ticketId && ticket.status && mktEventConfigMap[ticket.eventName]) {
                map[ticket.eventName] = _createEventTicketInfo(ticket.eventName, ticket);
            }
        }
    });

    return map;
}

function _createEventTicketInfo(eventName, ticket = getStorageItem(_getTicketName(eventName))) {
    return {
        eventName: eventName,
        ticket: ticket,
        paused: false,
        needsStatusCheck: true,
        goodToGo: false     // this is set to true when after a server check for event entry
    }                       //  there is < 60 of wait time. No need to check server again.
}

/**
 * Changes the status of the passed in ticket.
 *
 * @param eventTicketInfo - contains information about the ticket
 * @param changeTo - PRE_EVENT, IN_EVENT, POST_EVENT
 * @private
 */
function _changeStatus(eventTicketInfo, changeTo) {
    let ticket = eventTicketInfo.ticket,
        status = _decodeStatus(ticket.status);

    if (status.eventStatus !== changeTo) {
        let newRadix = _getRandomNumber(12, 37),
            newStatusPart = `${newRadix}${(((_decodeValue(status.origValues[3]) >> 3) << 3) | changeTo).toString(newRadix)}`;

        ticket.status = `${status.origValues[0]}-${status.origValues[1]}-${status.origValues[2]}-${newStatusPart}`;
        eventTicketInfo.needsStatusCheck = true;
        storage.setItem(_getTicketName(ticket.eventName), ticket, {ttl: TICKET_TTL});

        // if there was a timer associated with the old status clear it
        _clearTimeout(eventTicketInfo);
    }
}

/**
 * Gets a random number between min and max
 * @param min - inclusive min
 * @param max - exclusive max
 * @returns {Number}
 * @private
 */
function _getRandomNumber(min, max) {
    return Math.floor(Math.random() * (max - min)) + min; //The maximum is exclusive and the minimum is inclusive
}

/**
 * Decodes a status value.
 *
 * @param value - value to be decoded
 * @returns {number}
 * @private
 */
function _decodeValue(value) {
    return parseInt(value.substring(2), parseInt(value.substring(0, 2)));
}

/**
 * Creates a ticket name based on the eventName passed in.
 *
 * @param eventName
 * @returns {string}
 * @private
 */
function _getTicketName(eventName) {
    return `${eventName}-event-ticket`;
}

//=========================================================================================================//
//
//  Functions dealing with URL functionality and service calls
//
//=========================================================================================================//

/**
 * Either requests a new ticket or an updated event ticket from the AWS service
 *
 * @param eventTicketInfo - contains informatino about the ticket.
 *
 * @returns {Promise<*>} a new or updated ticket for the specified event
 * @private
 */
async function _getRemoteTicket(eventTicketInfo) {
    let eventName = eventTicketInfo.eventName,
        ticketId = eventTicketInfo.ticket ? eventTicketInfo.ticket.ticketId : null,
        ticketName = _getTicketName(eventName);

    try {
        let method = ticketId ? 'GET' : 'POST',
            seParams = method === 'POST' ? _getSeParams(eventName) : '',
            response = await axios({
                method: method,
                url: `${_getAwsUrl(eventName, ticketId)}${seParams}`,
                headers: _getHeaders(method === 'GET' ? true : false),
                timeout: 7000
            });

        eventTicketInfo.ticket = response.data.salesEventResponse;
        eventTicketInfo.needsStatusCheck = true;
        storage.setItem(ticketName, eventTicketInfo.ticket, {ttl: TICKET_TTL});
    } catch (err) {
        if (err.response && err.response.data.status === 404) {
            let [eventNotFound, ticketNotFound] = _whichResourceNotFound(err.response.data.messages, eventName, ticketId);

            if (ticketNotFound) {
                // The ticket doesn't exist in AWS anymore. Get a new one.
                eventTicketInfo.ticket = null;
                await _getRemoteTicket(eventTicketInfo);
            } else if (eventNotFound) {
                // The sales event definition doesn't exist anymore.
                if (eventTicketInfo.ticket) {
                    // This will cause the ticket to be removed locally.
                    console.log(`Expiring ticket ${ticketId} for event ${eventName}!`);
                    _changeStatus(eventTicketInfo, POST_EVENT);
                }
            }
        } else {
            console.error('salesEventService.getRemoteTicket - Sales event service error or timeout! Waiting to try again', err);
            // Timed out or some other error
            // Wait and then call again.
            await _wait(3000);
            await _getRemoteTicket(eventTicketInfo);
        }
    }

    return eventTicketInfo;
}

/**
 * Determines if the 404 was from an unknown eventName or an unknown ticketId
 *
 * @param errorMsgs
 * @param eventName
 * @param ticketId
 * @returns {boolean[]}
 * @private
 */
function _whichResourceNotFound(errorMsgs = [], eventName, ticketId) {
    let event = false,
        ticket = false;

    errorMsgs.forEach(msg => {
        if (msg.code === 'resourceNotFound') {
            if (msg.technicalMessage) {
                if (msg.technicalMessage.includes(eventName.toUpperCase())) {
                    event = true;
                } else if (ticketId && msg.technicalMessage.includes(ticketId)) {
                    ticket = true;
                }
            }
        }
    });

    return [event, ticket];
}

/**
 * Gets the URL for the event's catalog page. This is content in market configuration.
 *
 * @param eventName - the event to look up the event catalog page
 * @returns {string}
 * @private
 */
function _getEventUrl(eventName) {
    let url = '';

    if (mktEventConfigMap[eventName] && mktEventConfigMap[eventName].page) {
        url = `${UrlService.getLocaleUrl()}${mktEventConfigMap[eventName].page}`;
    }

    return url;
}

/**
 * Gets the AWS service URL based on the eventName and ticketId passed in.
 *
 * @param eventName
 * @param ticketId
 * @returns {string}
 * @private
 */
function _getAwsUrl(eventName, ticketId) {
    const awsUrl = UrlService.getAwsUrl();
    let url;

    if (eventName && ticketId) {    // update ticket
        url = `${awsUrl}/sales-event/v1/${eventName}/${ticketId}`;
        // url = `https://devapi.cloud.nuskin.com/sales-event/v1/${eventName}/${ticketId}`;
    } else if (eventName) {         // create ticket
        url = `${awsUrl}/sales-event/v1/${eventName}`;
        // url = `https://devapi.cloud.nuskin.com/sales-event/v1/${eventName}`;
    } else {                        // get list of events
        url = `${awsUrl}/sales-event/v1`;
        // url = `https://devapi.cloud.nuskin.com/sales-event/v1`;
    }

    return url;
}

/**
 * Creates a headers object for the AWS service call.
 *
 * @param clientId
 * @param clientSecret
 * @param jwt
 * @returns {{Accept: string, "Content-Type": string, client_id: *}}
 * @private
 */
function _getHeaders(noCache = false) {
    return {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'client_id': RunConfigService.getRunConfig().clientId,
        ...(noCache && {'Cache-Control': 'no-cache'}),
        ...(noCache && {'Pragma': 'no-cache'}),
        ...(noCache && {'Expires': '0'})
    };
}

async function _getActiveEvents() {
    let response = await axios({
        method: 'GET',
        url: `${_getAwsUrl()}?activeWithin=${ACTIVE_EVENT_WITHIN}`,
        headers: _getHeaders(true)
    });
    return response.data.salesEventResponse
}

//=========================================================================================================//
//
//  Misc functions
//
//=========================================================================================================//

function _clearPollingInterval() {
    if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
    }
}

function _clearActiveEventsTimer() {
    if (activeEventsTimerId) {
        clearTimeout(activeEventsTimerId);
    }
}

function _clearServiceData() {
    if (eventTicketInfoMap) {
        Object.values(eventTicketInfoMap).forEach((eventTicketInfo) => {
            _clearTimeout(eventTicketInfo);
        });
    }
    eventTicketInfoMap = null;
    activeEventMap = {};
    mktEventConfigMap = null;
    seParamEvents = null;
    _clearPollingInterval();
    _clearActiveEventsTimer();
    initEventTicketCallMap = {};
}

/**
 * Initializes global data used by service.
 *
 * @returns {boolean} flag representing if initialization occurred.
 * @private
 */
async function _initData(force) {
    let loaded = false;

    // initialize globals
    if (events.setValue && (force || (UrlService.isLocalePage() || storage.getItem('seParams')))) {
        _loadSeParamEventNames();
        await _loadMarketEventConfig(false);
        _loadLocalEventTickets();
        loaded = true;
    }
    return loaded;
}
/**
 * Initializes global data used by service for an specific market
 *
 * @private
 */
async function _initDataConnect(country) {
    // initialize globals
    _loadSeParamEventNames();
    await _loadMarketEventConfig(false,country);
    _loadLocalEventTickets();
    initActiveEvents(country).catch((err) => {console.error('Error initializing event tickets.', err)});
}

/**
 * Gets a mapping of sales events associated with the current market
 *
 * @private
 */
async function _loadMarketEventConfig(force, country=undefined) {
    if (force || !mktEventConfigMap) {
        try {
            const response = await axios({
                method: 'GET',
                url: `${_getAwsUrl()}`,
                headers: _getHeaders(true)
            });
            const salesEvents = response.data.salesEventResponse || [];
            mktEventConfigMap = {};
            country = country || RunConfigService.getRunConfig().country;
            salesEvents.forEach((event) => {
                if (event.markets && event.markets.includes(country)) {
                    mktEventConfigMap[event.eventName] = {
                        eventName: event.eventName,
                        page: event.page,
                        status: event.eventEnabled === true ? 'on' : 'off'
                    }
                }
            });

            if (!force) {
                events.setValue(events.salesevent.MARKET_CONFIG_EVENTS, [Object.keys(mktEventConfigMap)]);
            }
        } catch (e) {
            console.error('Error loading market events', e);
        }
    }
}

/**
 * Helper function for getting storage items
 *
 * @param key
 * @returns {*}
 */
function getStorageItem(key) {
    let item = null;

    try {
        item = storage.getItem(key);
    } catch(err) {
        // just let ticket be null;
    }

    return item;
}

async function _checkForMissingTicket(eventName) {
    if (mktEventConfigMap[eventName] && !eventTicketInfoMap[eventName]) {
        await _initEventTicketInfo(eventName);
    }
}

/**
 * This initializes an interval timer which checks to see if the browser (computer)
 *  went to sleep.  It's intention is to update the wait timer if it has.
 */
async function _initializePolling() {
    if (!intervalId) {
        const TIMEOUT = 10000;
        let lastTime = Date.now(),
            intervalCnt = 1;

        intervalId = setInterval(async () => {
            const now = Date.now();
            const slept = now > (lastTime + TIMEOUT + 3000)

            // if polling market config, only reload it every 30 seconds
            if (slept || pollMarketConfig && intervalCnt % 6 === 0) {
                await _loadMarketEventConfig(true);
                _marketStatusCheck();
            }
            intervalCnt++;

            // if been to sleep for more then 3 seconds then check all ticket statuses.
            if (slept) {
                // Went to sleep for 3 seconds or more
                Object.values(eventTicketInfoMap).forEach(eventTicketInfo => {
                    // A new timer needs to be set, clear current one if exists
                    eventTicketInfo.needsStatusCheck = true;
                    _clearTimeout(eventTicketInfo);
                });
            }

            Object.values(eventTicketInfoMap).forEach(eventTicketInfo => {
                _checkStatus(eventTicketInfo, false);
            });

            if (window.location.pathname === '/static/checkout/checkout.html' ||
                window.location.pathname === '/static/cart/cart.html'
            ) {
                // check for missing tickets. Only perform on checkout screen
                const activeEvents = await _getActiveEvents();

                for (const eventInfo of activeEvents) {
                    await _checkForMissingTicket(eventInfo.eventName);
                }
            }

            lastTime = now;
        }, TIMEOUT);
    }
}

function _marketStatusCheck(eventTicketInfo = null) {
    let eventTicketInfos = eventTicketInfo ? [eventTicketInfo] : eventTicketInfoMap;

    Object.values(eventTicketInfos).forEach(ticketInfo => {
        if (ticketInfo.ticket && !ticketInfo.ticket.manualTicket) {
            let mktEventConfig = mktEventConfigMap[ticketInfo.eventName],
                status = mktEventConfig ? mktEventConfig.status : 'off';

            switch (status) {
                case 'on':
                    ticketInfo.needsStatusCheck = ticketInfo.needsStatusCheck || ticketInfo.paused === true;
                    ticketInfo.paused = false;
                    break;
                case 'paused':
                    ticketInfo.needsStatusCheck = ticketInfo.needsStatusCheck || ticketInfo.paused === false;
                    ticketInfo.paused = true;
                    break;
                case 'off':
                    _changeStatus(ticketInfo, POST_EVENT);
                    break;
            }
        }
    });
}

function _clearTimeout(eventTicketInfo) {
    if (eventTicketInfo.timerId) {
        clearTimeout(eventTicketInfo.timerId);
        eventTicketInfo.timerId = null;
        eventTicketInfo.needsStatusCheck = true;
    }
}

/**
 * Gets the manually added parameters to be passed to the AWS service when creating a ticket
 *
 * @param eventName
 * @param firstParams - should it preface it with a '?' or a '&' character
 * @returns {string}
 * @private
 */
function _getSeParams(eventName, firstParams = true) {
    let retVal = '',
        seParams = storage.getItem({key: 'seParams', type: 'Required', storageType: 'localStorage' }),
        seEvent = seParams && typeof seParams.events === 'object' ? seParams.events[eventName] : null;

    if (seEvent) {
        seEvent.forEach((param, i) => {
            let key = Object.keys(param)[0];

            retVal += `${i === 0 && firstParams ? '?' : '&'}${key}=${param[key]}`;
        });
    }

    return retVal;
}

/**
 * Gets the event names from existing tickets created with seParams or a current
 *  seParams localStorage object.
 *
 * @returns {Array}
 * @private
 */
function _loadSeParamEventNames() {
    if (!seParamEvents) {
        let seParams = storage.getItem({key: 'seParams', type: 'Required', storageType: 'localStorage' }),
            events = seParams && typeof seParams.events === 'object' ? seParams.events : {},
            holder = {};

        // first examine existing tickets and see if they were manually created
        //  and add those eventNames to the list
        if (eventTicketInfoMap) {
            Object.values(eventTicketInfoMap).forEach(ticketInfo => {
                if (ticketInfo.ticket && ticketInfo.ticket.manualTicket) {
                    holder[ticketInfo.eventName] = true;
                }
            });
        }

        // now check seParams for events. using a map will dedup the results
        Object.keys(events).forEach(eventName => {
            holder[eventName] = true;
        });
        seParamEvents = Object.keys(holder);
    }
}

/**
 * Can be called to stop the execution of code for the delay time passed in.
 *
 * @param delay - amount of time in milliseconds for the returned promise to
 *                wait before being resolved.
 * @returns {Promise}
 * @private
 */
const _wait = (delay) => new Promise(resolve => setTimeout(resolve, delay));

function _addWaitCheck(value) {
    waitChecks.push(value);
}

function _removeWaitCheck(value) {
    waitChecks = waitChecks.filter(checkValue => checkValue !== value);
}

//=========================================================================================================//
//
//  Runtime functions
//
//=========================================================================================================//

/**
 * Gets a mapping of events for active or soon to be active events. The results
 *  are cached for 30 minutes to minimize calls to AWS.
 *
 * @returns list of active event names or events active within 30 minutes.
 */
async function _loadAwsActiveEvents(country) {
    let activeEventsName = `${ACTIVE_EVENTS}-${country? country: RunConfigService.getRunConfig().country}`,
        storageObj = null,
        ttl = storageObj ? storageObj.ttl - Date.now() : ACTIVE_EVENT_TTL,
        eventInfos = {},
        ok = true;

    /**
     * Scoped function to add eventNames to the return value. The list passed in might contain
     *  event objects from the server in which case use the eventName field within.
     *
     * @param eventNames
     */
    let addEvents = (eventNames) => {
        eventNames.forEach(_eventName => {
            let eventName = _eventName.eventName || _eventName;

            if (mktEventConfigMap[eventName]) {
                eventInfos[eventName] = _createEventTicketInfo(eventName);
            }
        });
    };

    if (!storageObj) {
        // No locally stored active event names. Get from server.
        storageObj = {events: []};
        storage.setItem(activeEventsName, storageObj, {ttl: 250}); // to prevent multiple calls.
        try {
            addEvents(await _getActiveEvents());
        } catch (e) {
            // an error occurred with the call, clear active events
            //  so it won't wait 30 minutes to try again
            storage.removeItem(activeEventsName);
            ok = false;
        }
    } else {
        addEvents(storageObj.events);
    }
    if (ok) {
        addEvents(seParamEvents);
        storage.setItem(activeEventsName, {events: Object.keys(eventInfos)}, {ttl: ttl});
        _clearActiveEventsTimer();
        if(!country){
            activeEventsTimerId = setTimeout(initActiveEvents, ttl + ONE_MINUTE);
        }
    }

    return eventInfos;
}

/**
 * Initializes tickets for active events.
 *
 * @returns {Promise<void>}
 */
async function initActiveEvents(country = undefined) {
    let promises = [];

    if (Object.values(mktEventConfigMap).find(mktEvent => {return mktEvent.status !== 'off'})) {
        let awsEvents = await _loadAwsActiveEvents(country);

        // if a ticket exists in localStorage for an event the is not active, remove it.
        Object.keys(eventTicketInfoMap).forEach((key) => {
            if (!awsEvents[key]) {
                storage.removeItem(_getTicketName(key));
                delete eventTicketInfoMap[key];
            }
        })

        eventTicketInfoMap = Object.assign({}, awsEvents, eventTicketInfoMap);
        Object.keys(eventTicketInfoMap).forEach(eventName => {
            if (mktEventConfigMap[eventName] && eventTicketInfoMap[eventName].eventName) {
                promises.push(_initEventTicketInfo(eventName));
            }
        });
    }

    return Promise.all(promises).then(async () => {

        events.setValue(events.salesevent.INITIAL_ACTIVE_EVENTS, [Object.keys(activeEventMap)]);

        await _initializePolling();
        _removeWaitCheck(INITIALIZING);
    });
}

/**
 * Can be called from outside of the salesEventService to force initialization.
 * By default the service doesn't initialize if not on a locale page.  There are
 * situations now where this service needs to run when not on a locale page.
 *
 * @param {boolean} force
 */
async function init(force = false) {
    await checkForWaits();

    _addWaitCheck(EXTERNAL_INITIALIZING);
    _clearServiceData();
    _init(force);
    _removeWaitCheck(EXTERNAL_INITIALIZING)
}
/**
 * Can be called from outside of the salesEventService to force initialization with an specific market.
 * By default the service doesn't initialize if not on a locale page.
 *
 * @param {String} country
 */

async function initEventsForConnect(country) {
    await checkForWaits();

    _addWaitCheck(EXTERNAL_INITIALIZING);
    _clearServiceData();
    await _initDataConnect(country);
    _removeWaitCheck(EXTERNAL_INITIALIZING)
}
/**
 *  * Initialization of active events on document ready

 * @param force
 * @private
 */
async function _init(force = true) {
    // init is passed arguments when initiated by $(document).ready(init) below. Need
    // to check for actual boolean true for initialization.
    if (await _initData(force === true)) {
        initActiveEvents().catch((err) => {console.error('Error initializing event tickets.', err)});
    } else {
        _removeWaitCheck(INITIALIZING);
    }
}

/**
 * Everytime the activeEvents map changes check to see if there are any tickets.
 *  If there are, make sure polling is happening, otherwise, stop polling.
 */
events.subscribe(events.salesevent.ACTIVE_EVENTS, (/*activeEvents*/) => {
    _initializePolling().then(() => console.log('Polling initialized'));
});

// persistentCartService needs to wait until the following configMaps are loaded
// before this service can be initialized.
const neededConfigMaps = ['Loyalty', 'Cart'];

const isSubset = (array1, array2) => {
    return array2.every((element) => array1.includes(element));
}


const csConfigCallback = async (configs) => {
    if (configs != null && isSubset(configs, neededConfigMaps)) {
        events.unsubscribe(events.global.CS_CONFIG_SET, csConfigCallback);
        _init(false).then(() => console.log('salesEventService initialized'));
    }
}

/**
 * Waits for config to be loaded and then initializes tickets
 * for events associated to the current market.
 */
events.getValue(events.global.CS_CONFIG_SET, csConfigCallback, true);
